import type { NoticeWithType } from "../components/ViewNotices";
import { resolveNoticeAudienceSize } from "./viewCount";
import type { Subcircle } from "../hooks/useSubcircles";
import { getUpcomingScheduledNotices } from "./noticeScheduling";
import { isNoticeHiddenFromPublishedList } from "./noticeFeedVisibility";
import { VIEW_NOTICES } from "../routes";

export type NoticeFeedTab = "scheduled" | "sent";

/** Query param that opens View Notices on a tab, e.g. `?tab=scheduled`. */
export const NOTICE_TAB_PARAM = "tab";

export const scheduledNoticesHref = () => `${VIEW_NOTICES}?${NOTICE_TAB_PARAM}=scheduled`;

/** Query params that open View Notices with its Type or Sender filter set. */
export const NOTICE_TYPE_PARAM = "type";
export const NOTICE_SENDER_PARAM = "sender";

/** The types the Type filter offers. */
const FILTER_TYPES = ["info", "urgent", "event", "image"];

export const urgentNoticesHref = () => `${VIEW_NOTICES}?${NOTICE_TYPE_PARAM}=urgent`;

/** Notices with the Sender filter set to a sender's name as the list shows it. */
export const noticesBySenderHref = (sender: string) =>
  `${VIEW_NOTICES}?${new URLSearchParams({ [NOTICE_SENDER_PARAM]: sender })}`;

export function parseNoticeFilterParams(searchParams: URLSearchParams): {
  type: string | null;
  sender: string | null;
} {
  const type = searchParams.get(NOTICE_TYPE_PARAM);
  return {
    type: type && FILTER_TYPES.includes(type) ? type : null,
    sender: searchParams.get(NOTICE_SENDER_PARAM)?.trim() || null,
  };
}

export function parseNoticeTabParam(searchParams: URLSearchParams): NoticeFeedTab | null {
  const value = searchParams.get(NOTICE_TAB_PARAM);
  return value === "scheduled" || value === "sent" ? value : null;
}

/**
 * View Notices' three lists: Drafts, the Sent tab and the Scheduled tab
 * (soonest first). Deleted notices are in none of them.
 */
export function splitNoticesForFeed<N extends NoticeWithType>(
  notices: N[],
  now: number = Date.now()
): { drafts: N[]; publishedNotices: N[]; scheduledNotices: N[] } {
  // getUpcomingScheduledNotices only filters and sorts, so it hands back the same notices
  const scheduledNotices = getUpcomingScheduledNotices(
    notices as unknown as Parameters<typeof getUpcomingScheduledNotices>[0],
    now
  ) as unknown as N[];
  const scheduledIds = new Set(scheduledNotices.map((n) => n.noticeId || n.id));
  const drafts: N[] = [];
  const publishedNotices: N[] = [];

  notices.forEach((notice) => {
    if (notice.isDeleted) return;
    if (notice.isDraft) {
      drafts.push(notice);
    } else if (
      !scheduledIds.has(notice.noticeId || notice.id) &&
      !isNoticeHiddenFromPublishedList(notice)
    ) {
      publishedNotices.push(notice);
    }
  });

  return { drafts, publishedNotices, scheduledNotices };
}

export interface NoticeFeedFilterState {
  searchQuery: string;
  selectedCircle: string;
  selectedType: string;
  selectedSender: string;
}

export function getReadableSenderName(
  sender: string | undefined,
  user: { userType?: string; orgId?: string; schoolName?: string } | null
): string {
  if (!sender) return "Unknown";

  if (
    sender.match(
      /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i
    )
  ) {
    return user?.userType === "team"
      ? user.orgId || "Organization"
      : user?.schoolName || "Organization";
  }

  return sender;
}

export function filterNoticesForFeed(
  notices: NoticeWithType[],
  filters: NoticeFeedFilterState,
  user: { userType?: string; orgId?: string; schoolName?: string } | null
): NoticeWithType[] {
  const { searchQuery, selectedCircle, selectedType, selectedSender } = filters;
  const query = searchQuery.toLowerCase();

  return notices.filter((notice) => {
    const matchesSearch =
      notice.title.toLowerCase().includes(query) ||
      notice.body.toLowerCase().includes(query);

    const matchesCircle =
      selectedCircle === "all" ||
      (selectedCircle === "main" && !notice.targetSubcircle) ||
      (selectedCircle !== "main" &&
        notice.targetSubcircle === selectedCircle);

    const matchesType = (() => {
      if (selectedType === "all") return true;
      if (selectedType === "urgent") return notice.isUrgent === true;
      const noticeType = notice.type as string;
      const normalizedType =
        noticeType === "image_notice" ? "image" : noticeType;
      return normalizedType === selectedType;
    })();

    const noticeSender = getReadableSenderName(notice.sender, user);
    const matchesSender =
      selectedSender === "all" || noticeSender === selectedSender;

    return matchesSearch && matchesCircle && matchesType && matchesSender;
  });
}

export interface NoticeFeedMetrics {
  totalNotices: number;
  scheduledCount: number;
  averageReadRate: number;
  activeCircles: number;
}

export function computeNoticeFeedMetrics(
  publishedNotices: NoticeWithType[],
  scheduledNotices: NoticeWithType[],
  subcircles: Subcircle[],
  orgMemberCount: number
): NoticeFeedMetrics {
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recentPublished = publishedNotices.filter(
    (notice) => new Date(notice.createdAt).getTime() >= thirtyDaysAgo
  );

  const readRates = recentPublished
    .map((notice) => {
      const total =
        (notice as NoticeWithType & { audienceTotal?: number }).audienceTotal ??
        resolveNoticeAudienceSize(
          notice.targetSubcircle,
          subcircles,
          orgMemberCount,
          notice.targetUserId
        );
      const seen = notice.readCount ?? 0;
      return total > 0 ? (seen / total) * 100 : null;
    })
    .filter((rate): rate is number => rate !== null);

  const averageReadRate =
    readRates.length > 0
      ? Math.round(
          readRates.reduce((sum, rate) => sum + rate, 0) / readRates.length
        )
      : 0;

  const activeCircleIds = new Set<string>();
  [...publishedNotices, ...scheduledNotices].forEach((notice) => {
    activeCircleIds.add(notice.targetSubcircle || "main");
  });

  return {
    totalNotices: publishedNotices.length + scheduledNotices.length,
    scheduledCount: scheduledNotices.length,
    averageReadRate,
    activeCircles: activeCircleIds.size,
  };
}
